import {
  EMPTY_PROJECT_SETTINGS,
  ProjectSettings,
  childEntityTypes,
} from '@stoaria-control/schema'
import type { SettingsLayer } from '@stoaria-control/schema'
import type { DetailSelection } from './entity-detail-selection.ts'
import type { HierarchyTree, PatchKind, TreeNode } from './hierarchy-api.ts'

/** Locating a record in the tree, and turning it into everything the detail
 * panel needs — which is mostly ANCESTOR facts (what it inherits, what its
 * parent permits), so the walk that finds it also collects the chain. */

export interface FoundNode {
  node: TreeNode
  /** Root first, INCLUDING the found node itself. */
  ancestors: TreeNode[]
}

export function findInTree(
  tree: HierarchyTree,
  _kind: PatchKind,
  id: string,
): FoundNode | null {
  const walk = (node: TreeNode, ancestors: TreeNode[]): FoundNode | null => {
    const chain = [...ancestors, node]
    if (node.id === id) return { node, ancestors: chain }
    for (const child of node.children) {
      const found = walk(child, chain)
      if (found) return found
    }
    return null
  }
  for (const root of tree.roots) {
    const found = walk(root, [])
    if (found) return found
  }
  return null
}

/** Records of one kind sitting on this record's own ancestor chain, nearest
 * ancestor last, each labelled by its path.
 *
 * Chain-scoped, NOT tree-scoped: offering a machine or a forge from an
 * unrelated branch invites bindings that no inheritance rule would ever
 * produce, and which nothing downstream expects to see. */
function collectOnChain(
  ancestors: { name: string; children?: TreeNode[] }[],
  kind: 'server' | 'repository',
): { id: string; name: string; path: string; details: unknown }[] {
  const out: { id: string; name: string; path: string; details: unknown }[] = []
  const trail: string[] = []
  for (const ancestor of ancestors) {
    trail.push(ancestor.name)
    for (const child of ancestor.children ?? [])
      if (child.kind === kind)
        out.push({
          id: child.id,
          name: child.name,
          path: [...trail, child.name].join(' > '),
          details: child.repositoryDetails,
        })
  }
  return out
}

function toSettingsLayer(node: {
  name: string
  projectSettings: unknown
}): SettingsLayer {
  return { name: node.name, settings: node.projectSettings }
}

/** The label an ancestor contributes to the template context — its own
 * entity type, else the scope level at its depth, else its bare kind. */
function labelFor(node: TreeNode, scopeLevelNames: string[]): string {
  if (node.kind === 'server') return 'server'
  if (node.entityType !== '') return node.entityType
  return scopeLevelNames[node.depth] ?? 'entity'
}

export function toSelection(found: FoundNode): DetailSelection {
  const { node, ancestors } = found
  const root = ancestors[0]
  const scopeLevels = root?.entityDetails.scopeLevels ?? []
  const scopeLevelNames = scopeLevels.map((level) => level.name)
  const parent = ancestors[ancestors.length - 2]

  // The chain includes the node itself, which must not appear as its own
  // ancestor.
  const ancestorNodes = ancestors.slice(0, -1)

  return {
    kind: node.kind,
    id: node.id,
    name: node.name,
    entityType: node.entityType,
    hostName: node.hostName,
    defaultBranch: node.defaultBranch,
    config: node.config,
    entityDetails: node.entityDetails,
    projectSettings: node.projectSettings,
    isTopLevel: node.parentId === null,
    depth: node.depth,
    parentId: parent?.id ?? null,
    // Computed by the caller, which holds the whole tree — filled in after
    // this returns.
    moveTargets: [],
    ancestorNames: [
      ...ancestorNodes.map((ancestor) => ancestor.name),
      node.name,
    ],
    ancestorConfigs: ancestorNodes.map((ancestor) => ancestor.config),
    ancestorLabels: ancestorNodes.map((ancestor) =>
      labelFor(ancestor, scopeLevelNames),
    ),
    ancestorSettings: ancestorNodes.map(toSettingsLayer),
    scopeLevels,
    // An entity's type options come from its PARENT, since that is what
    // names the child groups.
    entityTypeOptions:
      node.kind === 'server' || !parent
        ? []
        : childEntityTypes({
            details: parent.entityDetails,
            depth: parent.depth,
            scopeLevels,
          }),
    servers: collectOnChain(ancestors, 'server').map(({ id, name, path }) => ({
      id,
      name,
      path,
    })),
    repositories: collectOnChain(ancestors, 'repository'),
    repositoryDetails: node.repositoryDetails,
    launchable: node.launchable,
    verification:
      node.kind === 'entity'
        ? {
            cwdExists: node.cwdExists,
            cwdVerifiedAt: node.cwdVerifiedAt,
            repoExists: node.repoExists,
            githubDescription: node.githubDescription,
            githubVerifiedAt: node.githubVerifiedAt,
          }
        : null,
  }
}

/** Parses a stored settings layer for display, falling back to blanks rather
 * than throwing — see the note on `safeParse` in the detail panel. */
export function parseSettings(stored: unknown): ProjectSettings {
  const parsed = ProjectSettings.safeParse(stored)
  return parsed.success ? parsed.data : { ...EMPTY_PROJECT_SETTINGS }
}
